import React, { useContext, useEffect, useState, useRef } from 'react'
import 'chartjs-adapter-date-fns'
import { apiGet } from 'utils/apiFetch'
import pathObj from 'utils/pathObj'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import AuthContext from 'context/AuthContext'
import ODateRangePicker from 'components/shared/datePicker/ODateRangePicker'
import { Link } from 'react-router-dom'
import useToastContext from 'hooks/useToastContext'
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement
} from 'chart.js'
import { Bar, Doughnut, Line, Pie } from 'react-chartjs-2'

// Register ChartJS components
ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)

function CompanyHome () {
  const { t } = useTranslation()
  const notification = useToastContext()
  let { logoutUser, updatePageName } = useContext(AuthContext)
  const [dashboardDetails, setDashboardDetails] = useState({})
  const [startDate, setStartDate] = useState()
  const [endDate, setEndDate] = useState('')
  const [isReset, setIsReset] = useState(false)
  const [notificationShown, setNotificationShown] = useState(false)
  
  const handleDateChange = (start, end) => {
    setStartDate(start)
    setEndDate(end)
  }
  const formatDate = date => (date ? dayjs(date).format('YYYY-MM-DD') : null)

  const handleApiError = error => {
    console.log('error:', error)
    // GEO ACCESS: 401/409 session kick is handled by apiFetch (redirect to login).
    // Calling logoutUser here fired a second 401 and showed the CRA overlay.
    // if (
    //   error.response &&
    //   (error.response.status === 401 || error.response.status === 409)
    // ) {
    //   logoutUser()
    // }
  }

  const getDashboardDetails = async () => {
    try {
      const payload = {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate)
      }
      const path = pathObj.getDashboardDetails
      const result = await apiGet(path, payload)
      setDashboardDetails({ ...dashboardDetails, ...result.data.results })
    } catch (error) {
      console.log('error:', error)
      handleApiError(error)
    }
  }

  useEffect(() => {
    getDashboardDetails()
  }, [startDate, endDate])

  const handleReset = () => {
    setEndDate('')
    setStartDate('')
    setIsReset(true)
  }

  useEffect(() => {
    updatePageName(t('NAV_DASHBOARD'))
  }, [])

  // Employee Status Doughnut Chart Data
  const employeeStatusData = {
    labels: [
      t('ACCEPTED'),
      t('REJECTED'),
      t('PENDING'),
      t('DISCHARGED'),
      t('PROMOTED')
    ],
    datasets: [
      {
        data: [
          dashboardDetails?.totalEmployee?.totalAcceptedEmployee || 0,
          dashboardDetails?.totalEmployee?.totalRejectedEmployee || 0,
          dashboardDetails?.totalEmployee?.totalPendingEmployee || 0,
          dashboardDetails?.totalEmployee?.totalDischargedEmployee || 0,
          dashboardDetails?.totalEmployee?.totalPromotedEmployee || 0
        ],
        backgroundColor: [
          '#4BC0C0',
          '#FF6384',
          '#FFCE56',
          '#36A2EB',
          '#9966FF'
        ],
        borderColor: [
          '#4BC0C0',
          '#FF6384',
          '#FFCE56',
          '#36A2EB',
          '#9966FF'
        ],
        borderWidth: 1
      }
    ]
  }

  // Job Status Bar Chart Data
  const jobStatusData = {
    labels: [
      t('TOTAL_JOBS'),
      t('ACTIVE_JOBS'),
      t('INACTIVE_JOBS'),
      t('PENDING_JOBS')
    ],
    datasets: [
      {
        label: t('JOBS_STATUS'),
        data: [
          dashboardDetails?.totalJobs?.totalJobs || 0,
          dashboardDetails?.totalJobs?.totalActiveJobs || 0,
          dashboardDetails?.totalJobs?.totalInActiveJobs || 0,
          dashboardDetails?.totalJobs?.totalPendingJobs || 0
        ],
        backgroundColor: [
          'rgba(54, 162, 235, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(255, 99, 132, 0.6)',
          'rgba(255, 206, 86, 0.6)'
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(255, 206, 86, 1)'
        ],
        borderWidth: 1
      }
    ]
  }

  // Category Status Pie Chart Data
  const formData = {
    labels: [
      t('HIRING_FORMS'),
      t('ONBOARDING_FORMS')
    ],
    datasets: [
      {
        data: [
          // dashboardDetails?.totalCategory?.totalCategory || 0,
          dashboardDetails?.totalForms?.totalHiringForms || 0,
          dashboardDetails?.totalForms?.totalOnboardingForms || 0
        ],
        backgroundColor: [
          '#FF9F40',
          '#4BC0C0',
          '#FF6384'
        ],
        borderColor: [
          '#FF9F40',
          '#4BC0C0',
          '#FF6384'
        ],
        borderWidth: 1
      }
    ]
  }

  // Combined Onboarding Chart
  const onboardingData = {
    labels: [t('TOTAL_ONBOARDED'), t('HR_ONBOARDED'), t('ACCOUNTANT_ONBOARDED')],
    datasets: [
      {
        label: t('ONBOARDING_STATISTICS'),
        data: [
          dashboardDetails?.totalOnboarded || 0,
          dashboardDetails?.totalHrOnboarded || 0,
          dashboardDetails?.totalAccountantantOnboarded || 0
        ],
        backgroundColor: [
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 159, 64, 0.6)',
          'rgba(54, 162, 235, 0.6)'
        ],
        borderColor: [
         'rgba(54, 162, 235, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(54, 162, 235, 1)'
        ],
        borderWidth: 1
      }
    ]
  }

  // Chart options
  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom'
      }
    }
  }

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  }

  
  // Show toast notification when subscription data is available
  // useEffect(() => {
  //   if (dashboardDetails?.subscriptionNotification?.showToast && !notificationShown) {
  //     const subNotification = dashboardDetails.subscriptionNotification;
      
  //     // Show appropriate toast based on type
  //     switch (subNotification.toastType) {
  //       case 'error':
  //         notification.error(subNotification.message);
  //         break;
  //       case 'warning':
  //         notification.warn(subNotification.message);
  //         break;
  //       default:
  //         notification.info(subNotification.message);
  //     }
  //     // Mark notification as shown
  //     setNotificationShown(true);
  //   }
  // }, [dashboardDetails, notification, notificationShown]);

  return (
    <>
      <div className='sm:flex items-center text-center sm:text-left px-3 md:px-4 xl:px-7 lg:px-5 py-4 md:py-8 border'>
        <ODateRangePicker
          handleDateChange={handleDateChange}
          isReset={isReset}
          setIsReset={setIsReset}
        />
        <button
          type='button'
          onClick={handleReset}
          className='bg-LightBlue text-sm px-8 mb-3 ml-3 py-2 rounded-lg items-center border border-transparent text-white hover:bg-DarkBlue sm:w-auto w-1/2'
        >
          {t('O_RESET')}
        </button>
      </div>
      
      <div className='py-4 px-4 md:px-8'>
        {/* Summary Statistics Grid */}
      <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-5'>
          <Link to='/all-employees' onClick={() => updatePageName(t('TOTAL_ACCEPTED_EMPLOYEES'))} >
          <div className='bg-purple-50 p-4 rounded-lg border border-purple-100'>
            <h4 className='text-sm text-purple-700 font-medium mb-1'>{t('TOTAL_EMPLOYEES')}</h4>
            <p className='text-2xl font-bold text-purple-900'>
              {dashboardDetails?.totalEmployee?.totalEmployee || 0}
            </p>
          </div></Link>
          <Link to='/manage-jobs' state={'active'} onClick={() => updatePageName(t('MANAGE_JOBS'))} >
          <div className='bg-amber-50 p-4 rounded-lg border border-amber-100'>
            <h4 className='text-sm text-amber-700 font-medium mb-1'>{t('TOTAL_JOBS_CREATED')}</h4>
            <p className='text-2xl font-bold text-amber-900'>
              {dashboardDetails?.totalJobs?.totalJobs  || 0}
            </p>
          </div></Link>
          <Link to='/HR-onboarding' onClick={() => updatePageName(t('HR_ONBOARDING'))} >
          <div className='bg-blue-50 p-4 rounded-lg border border-blue-100'>
            <h4 className='text-sm text-blue-700 font-medium mb-1'>{t('TOTAL_HR_ONBOARDED')}</h4>
            <p className='text-2xl font-bold text-blue-900'>
              {dashboardDetails?.totalHrOnboarded || 0}
            </p>
          </div></Link>
          <Link to='/accountant-onboarding' onClick={() => updatePageName(t('ACCOUNTANT_ONBOARDING'))} >
          <div className='bg-green-50 p-4 rounded-lg border border-green-100'>
            <h4 className='text-sm text-green-700 font-medium mb-1'>{t('TOTAL_ACCOUNTANT_ONBOARDED')}</h4>
            <p className='text-2xl font-bold text-green-900'>
              {dashboardDetails?.totalAccountantantOnboarded || 0}
            </p>
          </div></Link>
      </div>
        {/* Charts Section */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 mb-7'>
          {/* Employee Status Chart */}
          <div className='bg-white p-4 rounded-lg border shadow-sm'>
            <h3 className='text-lg font-semibold mb-4'>{t('EMPLOYEE_FORMS_STATUS')}</h3>
            <div className='h-64'>
              <Doughnut data={employeeStatusData} options={doughnutOptions} />
            </div>
          </div>

          {/* Job Status Chart */}
          <div className='bg-white p-4 rounded-lg border shadow-sm'>
            <h3 className='text-lg font-semibold mb-4'>{t('JOB_STATUS_OVERVIEW')}</h3>
            <div className='h-64'>
              <Bar data={jobStatusData} options={barOptions} />
            </div>
          </div>

          {/* Onboarding Statistics */}
          <div className='bg-white p-4 rounded-lg border shadow-sm'>
            <h3 className='text-lg font-semibold mb-4'>{t('ONBOARDING_STATISTICS')}</h3>
            <div className='h-64'>
              <Bar data={onboardingData} options={barOptions} />
            </div>
          </div>

          {/* Forms Chart */}
          <div className='bg-white p-4 rounded-lg border shadow-sm'>
            <h3 className='text-lg font-semibold mb-4'>{t('FORMS_DISTRIBUTION')}</h3>
            <div className='h-64'>
              <Pie data={formData} options={doughnutOptions} />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default CompanyHome